import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { SEOHead, LOCAL_BUSINESS_SCHEMA } from '../components/SEOHead';
import { ZapIcon, GlobeIcon, ArrowRightIcon, CheckCircleIcon, SearchIcon, MessageSquareIcon, TargetIcon, MapPinIcon, ChevronDownIcon, PhoneIcon, UsersIcon } from 'lucide-react';
import { ScrollReveal } from '../components/ScrollReveal';
const faqs = [{
  question: 'What does Viridia Analytics do?',
  answer: 'Viridia Analytics is a specialized digital marketing agency in Phoenix. We build predictable growth engines for local service businesses using a combination of targeted ad campaigns, AI-powered lead conversion, local SEO, and custom automation systems.'
}, {
  question: 'What industries does Viridia Analytics serve?',
  answer: 'We specialize in home services and local businesses, including HVAC, plumbing, roofing, landscaping, pest control, electrical, painting, remodeling, and med spas.'
}, {
  question: 'Where is Viridia Analytics located?',
  answer: 'We are headquartered in Phoenix, Arizona. We partner with home service businesses across the Greater Phoenix metro area (Scottsdale, Glendale, Mesa, Chandler, etc.) and Southern California.'
}, {
  question: 'What makes Viridia Analytics different from other marketing agencies?',
  answer: 'We are former Yelp insiders with deep knowledge of local search algorithms. We also go beyond just generating leads - we implement AI speed-to-lead systems to ensure those leads actually convert into booked jobs. We provide full-stack solutions: ads, AI, web, and CRM.'
}, {
  question: 'How much does it cost to work with Viridia Analytics?',
  answer: 'Our packages start at competitive rates tailored to your specific needs and market. We offer a free strategy call to audit your current marketing and build a custom plan. We do not lock you into long-term contracts.'
}, {
  question: 'Does Viridia Analytics offer AI-powered lead follow-up?',
  answer: 'Yes. Our AI systems respond to text messages, Yelp inquiries, and web forms in under 60 seconds, 24/7. We also implement missed call text-back so you never lose a lead to a competitor because you were on a job.'
}, {
  question: 'How do I get started with Viridia Analytics?',
  answer: 'Book a free strategy call with us. We will audit your current marketing, review your website and ad accounts, and show you exactly where you are leaving money on the table. Then, we build a custom plan to fix it.'
}];
const FAQItem = ({
  question,
  answer
}: {
  question: string;
  answer: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-gray-800 last:border-0" data-id="element-1730">
      <button onClick={() => setIsOpen(!isOpen)} className="w-full py-6 flex items-center justify-between text-left focus:outline-none group" data-id="element-1731">
        <span className="text-lg font-bold text-white group-hover:text-viridia transition-colors" data-id="element-1732">
          {question}
        </span>
        <ChevronDownIcon className={`w-5 h-5 text-gray-400 transition-transform duration-300 flex-shrink-0 ml-4 ${isOpen ? 'rotate-180 text-viridia' : ''}`} data-id="element-1733" />
      </button>
      <AnimatePresence data-id="element-1734">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeInOut'
      }} className="overflow-hidden" data-id="element-1735">
            <p className="pb-6 text-gray-400 leading-relaxed" data-id="element-1736">{answer}</p>
          </motion.div>}
      </AnimatePresence>
    </div>;
};
export function HomePage() {
  return <div className="bg-viridia-black min-h-screen" data-id="element-1737">
      <SEOHead title="Viridia Analytics | Marketing & AI for Local Service Businesses" description="We help local service businesses stop missing leads, respond instantly, and book more jobs using AI and ads. Real results for Phoenix-area contractors." canonical="/" keywords="marketing agency Phoenix, AI lead conversion, Yelp ads management, home services marketing, contractor marketing" jsonLd={LOCAL_BUSINESS_SCHEMA} faq={faqs} data-id="element-1738" />

      {/* HERO SECTION */}
      <section className="relative pt-32 pb-16 lg:pt-48 lg:pb-24 overflow-hidden" data-id="element-1739">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-1740">
          <div className="max-w-4xl mx-auto text-center" data-id="element-1741">
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5
          }} className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-white leading-tight mb-6 tracking-tight" data-id="element-1742">
              Turn Leads Into Booked Jobs Using AI and Ads
            </motion.h1>

            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-xl sm:text-2xl text-gray-300 mb-6 leading-relaxed" data-id="element-1743">
              We help local service businesses stop missing calls, respond to
              inquiries instantly, and automate follow-up so you can focus on
              the work.
            </motion.p>

            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="text-lg text-viridia font-medium mb-10" data-id="element-1744">
              Used by Phoenix-area contractors to generate leads, respond
              instantly, and book more estimates.
            </motion.p>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} data-id="element-1745">
              <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-base font-bold text-viridia-black bg-white rounded-lg hover:bg-gray-200 transition-all duration-300" data-id="element-1746">
                See what's leaking leads in your pipeline
                <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-1747" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* PROOF STRIP */}
      <section className="py-12 bg-viridia-surface border-y border-gray-800" data-id="element-1748">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1749">
          <ScrollReveal data-id="element-1750">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 text-center" data-id="element-1751">
              <div data-id="element-1752">
                <p className="text-3xl font-bold text-white mb-1" data-id="element-1753">242</p>
                <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold" data-id="element-1754">
                  Yelp Leads in 30 Days
                </p>
                <p className="text-xs text-viridia mt-1" data-id="element-1755">TFP Landscaping</p>
              </div>
              <div data-id="element-1756">
                <p className="text-3xl font-bold text-white mb-1" data-id="element-1757">28</p>
                <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold" data-id="element-1758">
                  AI-Booked Estimates
                </p>
                <p className="text-xs text-viridia mt-1" data-id="element-1759">TFP Landscaping</p>
              </div>
              <div data-id="element-1760">
                <p className="text-3xl font-bold text-white mb-1" data-id="element-1761">39.9%</p>
                <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold" data-id="element-1762">
                  Close Rate
                </p>
                <p className="text-xs text-viridia mt-1" data-id="element-1763">TFP Landscaping</p>
              </div>
              <div data-id="element-1764">
                <p className="text-3xl font-bold text-white mb-1" data-id="element-1765">138</p>
                <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold" data-id="element-1766">
                  Yelp Leads in 30 Days
                </p>
                <p className="text-xs text-orange-500 mt-1" data-id="element-1767">
                  Finish Coat Painting
                </p>
              </div>
              <div data-id="element-1768">
                <p className="text-3xl font-bold text-white mb-1" data-id="element-1769">29</p>
                <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold" data-id="element-1770">
                  AI-Booked Estimates
                </p>
                <p className="text-xs text-orange-500 mt-1" data-id="element-1771">
                  Finish Coat Painting
                </p>
              </div>
              <div data-id="element-1772">
                <p className="text-3xl font-bold text-white mb-1" data-id="element-1773">$15.05k</p>
                <p className="text-xs text-gray-400 uppercase tracking-wider font-semibold" data-id="element-1774">
                  Closed-Won Revenue
                </p>
                <p className="text-xs text-orange-500 mt-1" data-id="element-1775">
                  From AI-worked leads only
                </p>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* WHAT VIRIDIA ACTUALLY DOES */}
      <section className="py-24 bg-viridia-black" data-id="element-1776">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1777">
          <ScrollReveal className="text-center max-w-3xl mx-auto mb-16" data-id="element-1778">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4" data-id="element-1779">
              What We Actually Do
            </h2>
            <p className="text-xl text-gray-400" data-id="element-1780">
              We fix the operational gaps that cause local businesses to lose
              money.
            </p>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1781">
            {[{
            title: 'Yelp Ads Management',
            desc: 'We run GEO-targeted campaigns so you get more local visibility and more high-intent leads.',
            icon: <TargetIcon className="w-6 h-6 text-viridia" data-id="element-1782" />
          }, {
            title: 'AI Response Systems',
            desc: 'Every Yelp message, text, and form fill gets an instant reply so you never miss an inquiry.',
            icon: <MessageSquareIcon className="w-6 h-6 text-viridia" data-id="element-1783" />
          }, {
            title: 'AI Quote Booking',
            desc: 'Our AI qualifies prospects and books estimates directly on your calendar without human intervention.',
            icon: <ZapIcon className="w-6 h-6 text-viridia" data-id="element-1784" />
          }, {
            title: 'Missed-Call Recovery',
            desc: 'When you are on a job and miss a call, our system immediately texts the customer back to save the lead.',
            icon: <PhoneIcon className="w-6 h-6 text-viridia" data-id="element-1785" />
          }, {
            title: 'CRM Follow-Up Automation',
            desc: 'Leads get nurtured automatically over time, increasing your overall lead-to-job conversion rate.',
            icon: <UsersIcon className="w-6 h-6 text-viridia" data-id="element-1786" />
          }, {
            title: 'Web & Local SEO',
            desc: 'We build fast, conversion-focused websites so you are found when people search for your services.',
            icon: <GlobeIcon className="w-6 h-6 text-viridia" data-id="element-1787" />
          }].map((service, index) => <ScrollReveal key={index} delay={index * 0.1} data-id="element-1788">
                <div className="bg-viridia-surface border border-gray-800 rounded-xl p-6 h-full" data-id="element-1789">
                  <div className="w-12 h-12 rounded-lg bg-viridia-black border border-gray-800 flex items-center justify-center mb-4" data-id="element-1790">
                    {service.icon}
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2" data-id="element-1791">
                    {service.title}
                  </h3>
                  <p className="text-gray-400 leading-relaxed" data-id="element-1792">
                    {service.desc}
                  </p>
                </div>
              </ScrollReveal>)}
          </div>
        </div>
      </section>

      {/* FEATURED CASE STUDIES */}
      <section className="py-24 bg-viridia-surface border-y border-gray-800" data-id="element-1793">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1794">
          <ScrollReveal data-id="element-1795">
            <div className="flex flex-col md:flex-row justify-between items-end mb-12" data-id="element-1796">
              <div className="max-w-2xl" data-id="element-1797">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-1798">
                  Documented Results
                </h2>
                <p className="text-lg text-gray-400" data-id="element-1799">
                  Real numbers from real local service businesses.
                </p>
              </div>
              <Link to="/case-studies" className="mt-6 md:mt-0 inline-flex items-center text-white hover:text-gray-300 font-semibold transition-colors" data-id="element-1800">
                View Proof Library
                <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-1801" />
              </Link>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1802">
            <ScrollReveal delay={0.1} data-id="element-1803">
              <div className="bg-viridia-black border border-gray-800 rounded-2xl p-8 h-full flex flex-col justify-between" data-id="element-1804">
                <div data-id="element-1805">
                  <div className="inline-flex items-center px-3 py-1 rounded-full bg-blue-500/10 text-blue-400 text-xs font-bold tracking-wide uppercase mb-6 border border-blue-500/20" data-id="element-1806">
                    Landscaping
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4" data-id="element-1807">
                    TFP Landscaping
                  </h3>
                  <p className="text-gray-400 mb-6 leading-relaxed" data-id="element-1808">
                    <strong data-id="element-1809">Challenge:</strong> 4+ hour response time meant Yelp
                    ad spend was being wasted because leads went cold before the
                    owner could reply.
                  </p>
                  <div className="grid grid-cols-2 gap-4 mb-8" data-id="element-1810">
                    <div className="bg-viridia-surface p-4 rounded-lg border border-gray-800" data-id="element-1811">
                      <p className="text-2xl font-bold text-blue-500" data-id="element-1812">284%</p>
                      <p className="text-xs text-gray-400" data-id="element-1813">More Leads</p>
                    </div>
                    <div className="bg-viridia-surface p-4 rounded-lg border border-gray-800" data-id="element-1814">
                      <p className="text-2xl font-bold text-blue-500" data-id="element-1815">242</p>
                      <p className="text-xs text-gray-400" data-id="element-1816">Total Leads</p>
                    </div>
                    <div className="bg-viridia-surface p-4 rounded-lg border border-gray-800" data-id="element-1817">
                      <p className="text-2xl font-bold text-blue-500" data-id="element-1818">28</p>
                      <p className="text-xs text-gray-400" data-id="element-1819">AI Bookings</p>
                    </div>
                    <div className="bg-viridia-surface p-4 rounded-lg border border-gray-800" data-id="element-1820">
                      <p className="text-2xl font-bold text-blue-500" data-id="element-1821">39.9%</p>
                      <p className="text-xs text-gray-400" data-id="element-1822">Close Rate</p>
                    </div>
                  </div>
                </div>
                <Link to="/case-studies/tfp-landscaping" className="inline-flex items-center text-white font-semibold hover:text-gray-300 transition-colors" data-id="element-1823">
                  Read Full Case Study
                  <ArrowRightIcon className="w-4 h-4 ml-2" data-id="element-1824" />
                </Link>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2} data-id="element-1825">
              <div className="bg-viridia-black border border-gray-800 rounded-2xl p-8 h-full flex flex-col justify-between" data-id="element-1826">
                <div data-id="element-1827">
                  <div className="inline-flex items-center px-3 py-1 rounded-full bg-orange-500/10 text-orange-400 text-xs font-bold tracking-wide uppercase mb-6 border border-orange-500/20" data-id="element-1828">
                    Painting
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4" data-id="element-1829">
                    Finish Coat Painting
                  </h3>
                  <p className="text-gray-400 mb-6 leading-relaxed" data-id="element-1830">
                    <strong data-id="element-1831">Challenge:</strong> Generating Yelp demand but
                    couldn't keep up with inbound conversations. Leads were
                    slipping through the cracks.
                  </p>
                  <div className="grid grid-cols-2 gap-4 mb-8" data-id="element-1832">
                    <div className="bg-viridia-surface p-4 rounded-lg border border-gray-800" data-id="element-1833">
                      <p className="text-2xl font-bold text-orange-500" data-id="element-1834">29</p>
                      <p className="text-xs text-gray-400" data-id="element-1835">
                        AI-Booked Estimates
                      </p>
                    </div>
                    <div className="bg-viridia-surface p-4 rounded-lg border border-gray-800" data-id="element-1836">
                      <p className="text-2xl font-bold text-orange-500" data-id="element-1837">
                        $15.05k
                      </p>
                      <p className="text-xs text-gray-400" data-id="element-1838">Closed-Won</p>
                    </div>
                    <div className="bg-viridia-surface p-4 rounded-lg border border-gray-800" data-id="element-1839">
                      <p className="text-2xl font-bold text-orange-500" data-id="element-1840">138</p>
                      <p className="text-xs text-gray-400" data-id="element-1841">Total Leads</p>
                    </div>
                    <div className="bg-viridia-surface p-4 rounded-lg border border-gray-800" data-id="element-1842">
                      <p className="text-2xl font-bold text-orange-500" data-id="element-1843">64%</p>
                      <p className="text-xs text-gray-400" data-id="element-1844">Lead Growth</p>
                    </div>
                  </div>
                </div>
                <Link to="/case-studies/finish-coat-painting" className="inline-flex items-center text-white font-semibold hover:text-gray-300 transition-colors" data-id="element-1845">
                  Read Full Case Study
                  <ArrowRightIcon className="w-4 h-4 ml-2" data-id="element-1846" />
                </Link>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 bg-viridia-black" data-id="element-1847">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1848">
          <ScrollReveal className="text-center max-w-3xl mx-auto mb-16" data-id="element-1849">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-4" data-id="element-1850">
              How It Works
            </h2>
            <p className="text-xl text-gray-400" data-id="element-1851">
              A simple, 3-step system to capture and convert local demand.
            </p>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative" data-id="element-1852">
            {/* Connecting Line for Desktop */}
            <div className="hidden md:block absolute top-12 left-[15%] right-[15%] h-0.5 bg-gray-800 z-0" data-id="element-1853"></div>

            {[{
            step: '1',
            title: 'Drive Demand',
            desc: 'We manage targeted Yelp Ads and local SEO to put your business in front of high-intent customers in your service area.',
            icon: <TargetIcon className="w-6 h-6 text-white" data-id="element-1854" />
          }, {
            step: '2',
            title: 'Respond Instantly',
            desc: 'Our AI handles every inquiry - text, Yelp message, or missed call - within seconds, replacing slow human follow-up.',
            icon: <ZapIcon className="w-6 h-6 text-white" data-id="element-1855" />
          }, {
            step: '3',
            title: 'Book & Follow Up',
            desc: 'Estimates are scheduled automatically on your calendar, and the CRM nurtures any leads that do not book right away.',
            icon: <CheckCircleIcon className="w-6 h-6 text-white" data-id="element-1856" />
          }].map((item, index) => <ScrollReveal key={index} delay={index * 0.1} className="relative z-10" data-id="element-1857">
                <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-8 h-full" data-id="element-1858">
                  <div className="w-16 h-16 rounded-full bg-viridia-black border border-gray-700 flex items-center justify-center mb-6 mx-auto" data-id="element-1859">
                    {item.icon}
                  </div>
                  <div className="text-center" data-id="element-1860">
                    <h3 className="text-xl font-bold text-white mb-3" data-id="element-1861">
                      {item.step}. {item.title}
                    </h3>
                    <p className="text-gray-400 leading-relaxed" data-id="element-1862">{item.desc}</p>
                  </div>
                </div>
              </ScrollReveal>)}
          </div>
        </div>
      </section>

      {/* AUTHORITY / FOUNDER */}
      <section className="py-24 bg-viridia-black border-t border-gray-800" data-id="element-1863">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1864">
          <ScrollReveal data-id="element-1865">
            <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-8 md:p-12 flex flex-col md:flex-row gap-10 items-center" data-id="element-1866">
              <div className="w-32 h-32 md:w-48 md:h-48 rounded-full bg-gray-800 flex-shrink-0 overflow-hidden border-4 border-viridia-black shadow-xl" data-id="element-1867">
                <img src="https://cdn.magicpatterns.com/uploads/dBgonnHgNocdZqWCx2rT29/kevin-rogers.jpg" alt="Kevin Rogers" className="w-full h-full object-cover" onError={e => {
                e.currentTarget.src = 'https://ui-avatars.com/api/?name=Kevin+Rogers&background=1A1A1A&color=fff&size=200';
              }} data-id="element-1868" />
              </div>
              <div data-id="element-1869">
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-4" data-id="element-1870">
                  Built by Operators, for Operators
                </h2>
                <p className="text-gray-400 leading-relaxed mb-6" data-id="element-1871">
                  "As a former Yelp insider, I saw exactly why local service
                  businesses were losing money. They were buying ads, but they
                  didn't have the systems to respond to the leads fast enough. I
                  built Viridia Analytics here in Phoenix to fix that
                  operational gap - combining targeted visibility with instant
                  AI follow-up."
                </p>
                <p className="text-white font-bold" data-id="element-1872">Kevin Rogers</p>
                <p className="text-sm text-gray-500" data-id="element-1873">
                  Founder, Viridia Analytics
                </p>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="py-24 bg-viridia-surface border-t border-gray-800" data-id="element-1874">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-16" data-id="element-1875">
          <ScrollReveal data-id="element-1876">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-1877">
              Frequently Asked Questions
            </h2>
          </ScrollReveal>
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1878">
          <ScrollReveal delay={0.2} data-id="element-1879">
            <div className="bg-viridia-black border border-gray-800 rounded-2xl p-6 md:p-8" data-id="element-1880">
              {faqs.map((faq, index) => <FAQItem key={index} question={faq.question} answer={faq.answer} data-id="element-1881" />)}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 bg-viridia-black border-t border-gray-800" data-id="element-1882">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1883">
          <ScrollReveal data-id="element-1884">
            <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-6" data-id="element-1885">
              See what's leaking leads in your pipeline
            </h2>
            <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto" data-id="element-1886">
              Book a real strategy call. We'll look at your ads, website, and
              follow-up process to find exactly where you are losing jobs.
            </p>
            <Link to="/contact" className="inline-flex items-center justify-center px-10 py-5 text-lg font-bold text-viridia-black bg-white rounded-xl hover:bg-gray-200 transition-all duration-300" data-id="element-1887">
              Get a Real Strategy Call
              <ArrowRightIcon className="ml-2 w-6 h-6" data-id="element-1888" />
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>;
}